// Gravar a edição de um plano sem o recriar.
//
// O servidor já não aceita "substituir tudo": o Id de cada dia e de cada exercício é o que o histórico de
// execuções usa para ligar ao plano, e recriá-los a cada gravação partia essa ligação. Os ecrãs continuam a
// editar um rascunho completo (é o que dá uma boa experiência), e é isto que o traduz em operações pequenas:
// compara o plano que o servidor tem com o rascunho e só envia a diferença, pela ordem que o servidor aceita.
//
// O rascunho é compatível com dois formatos de exercício: o estruturado (setsTarget, repsMin, repsMax,
// restSeconds, targetWeightKg, tempo) e o antigo (sets, reps, weight, restTime), que o separador antigo do
// AdminDashboard ainda usa. O estruturado manda quando existe.

const isBlank = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

const toInt = (v) => {
  if (isBlank(v)) return null;
  const n = Number(String(v).trim().replace(',', '.'));
  return Number.isFinite(n) ? Math.trunc(n) : NaN;
};

const toNumber = (v) => {
  if (isBlank(v)) return null;
  const n = Number(String(v).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

/** "10" → {min: 10, max: 10}; "8-12", "8–12", "8 a 12" → {min: 8, max: 12}; senão nulo. */
export const parseRepsRange = (text) => {
  if (isBlank(text)) return null;
  const t = String(text).trim();
  let m = /^(\d{1,3})$/.exec(t);
  if (m) return { min: Number(m[1]), max: Number(m[1]) };
  m = /^(\d{1,3})\s*(?:-|–|—|a)\s*(\d{1,3})$/i.exec(t);
  if (m) return { min: Number(m[1]), max: Number(m[2]) };
  return null;
};

/** "40", "40kg", "42,5 kg" → quilos; "peso corporal", "20-25kg" → nulo (não é UM peso). */
export const parseWeightKg = (text) => {
  if (isBlank(text)) return null;
  const m = /^(\d{1,4}(?:[.,]\d{1,2})?)\s*(?:kg|kgs)?$/i.exec(String(text).trim());
  if (!m) return null;
  const kg = Number(m[1].replace(',', '.'));
  return kg > 0 ? kg : null;
};

/** "60", "90s", "1:30" → segundos; senão nulo. */
export const parseRestSeconds = (text) => {
  if (isBlank(text)) return null;
  const t = String(text).trim();
  let m = /^(\d{1,4})\s*(?:s|seg|segs)?$/i.exec(t);
  if (m) return Number(m[1]);
  m = /^(\d{1,2})\s*:\s*([0-5]\d)$/.exec(t);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  return null;
};

/** "3 × 10–12 · 90s · 40kg" — o mesmo texto que o servidor devolve em prescriptionSummary, para o rascunho. */
export const formatPrescription = (ex) => {
  const f = prescriptionOf(ex).value;
  const parts = [];
  let reps = null;
  if (f.repsMin != null) {
    const hi = f.repsMax ?? f.repsMin;
    reps = hi > f.repsMin ? `${f.repsMin}–${hi}` : `${f.repsMin}`;
    if (ex.trackingType === 'time') reps += 's';
  }
  if (f.setsTarget != null && reps) parts.push(`${f.setsTarget} × ${reps}`);
  else if (reps) parts.push(reps);
  else if (f.setsTarget != null) parts.push(`${f.setsTarget} séries`);
  if (f.restSeconds != null) parts.push(`${f.restSeconds}s`);
  if (f.targetWeightKg != null) parts.push(`${String(f.targetWeightKg).replace('.', ',')}kg`);
  if (f.tempo) parts.push(f.tempo);
  return parts.join(' · ');
};

/**
 * A prescrição de um exercício do rascunho, no formato do servidor, e o que não se percebeu.
 * Devolve { value: {setsTarget, repsMin, repsMax, restSeconds, targetWeightKg, tempo}, unparsed: [campos] }.
 */
export const prescriptionOf = (ex) => {
  const unparsed = [];
  const value = {};

  const structured = ex.setsTarget !== undefined || ex.repsMin !== undefined || ex.repsMax !== undefined
    || ex.restSeconds !== undefined || ex.targetWeightKg !== undefined;

  if (structured) {
    value.setsTarget = toInt(ex.setsTarget);
    value.repsMin = toInt(ex.repsMin);
    value.repsMax = toInt(ex.repsMax);
    value.restSeconds = toInt(ex.restSeconds);
    value.targetWeightKg = toNumber(ex.targetWeightKg);
  } else {
    value.setsTarget = toInt(ex.sets);
    const reps = parseRepsRange(ex.reps);
    if (!isBlank(ex.reps) && !reps) unparsed.push('reps');
    value.repsMin = reps ? reps.min : null;
    value.repsMax = reps ? reps.max : null;
    const rest = parseRestSeconds(ex.restTime);
    if (!isBlank(ex.restTime) && rest == null) unparsed.push('restTime');
    value.restSeconds = rest;
    const kg = parseWeightKg(ex.weight);
    if (!isBlank(ex.weight) && kg == null) unparsed.push('weight');
    value.targetWeightKg = kg;
  }

  value.tempo = isBlank(ex.tempo) ? null : String(ex.tempo).trim();
  for (const key of ['setsTarget', 'repsMin', 'repsMax', 'restSeconds', 'targetWeightKg']) {
    if (Number.isNaN(value[key])) { unparsed.push(key); value[key] = null; }
  }
  // Um alvo fixo é "10–10": sem máximo, o máximo é o mínimo.
  if (value.repsMin != null && value.repsMax == null) value.repsMax = value.repsMin;
  return { value, unparsed };
};

const clean = (v) => (isBlank(v) ? null : String(v).trim());
const sameText = (a, b) => (clean(a) ?? '') === (clean(b) ?? '');
const sameNum = (a, b) => (a ?? null) === (b ?? null);
const dateOnly = (v) => (isBlank(v) ? null : String(v).slice(0, 10));

/** O corpo de um exercício novo: o que o servidor aceita ao criar, a partir do rascunho. */
export const newExerciseBody = (ex, position) => {
  const { value, unparsed } = prescriptionOf(ex);
  const body = {
    name: clean(ex.name) || undefined,
    exerciseLibraryId: ex.exerciseLibraryId || null,
    setsTarget: value.setsTarget ?? undefined,
    repsMin: value.repsMin ?? undefined,
    repsMax: value.repsMax ?? undefined,
    restSeconds: value.restSeconds ?? undefined,
    targetWeightKg: value.targetWeightKg ?? undefined,
    tempo: value.tempo ?? undefined,
    notes: clean(ex.notes) ?? undefined,
    videoUrl: clean(ex.videoUrl) ?? undefined,
    imageUrl: clean(ex.imageUrl) ?? undefined,
    gifUrl: clean(ex.gifUrl) ?? undefined,
  };
  if (position != null) body.position = position;
  // Texto antigo que não é um número (ex.: "peso corporal") não se perde: vai para as notas do exercício.
  if (unparsed.includes('weight') && !isBlank(ex.weight)) body.notes = [body.notes, `Carga: ${String(ex.weight).trim()}`].filter(Boolean).join(' · ');
  return body;
};

/** O que mudou num exercício que já existe, como alteração parcial; vazio se não mudou nada. */
export const exercisePatch = (original, draft) => {
  const patch = {};
  const warnings = [];
  const { value, unparsed } = prescriptionOf(draft);

  if (!sameText(original.name, draft.name) && !isBlank(draft.name)) patch.name = String(draft.name).trim();
  if (!sameText(original.notes, draft.notes)) patch.notes = clean(draft.notes);
  // Um formulário que não tem cadência (o separador antigo) não a conhece: não a apaga.
  if (draft.tempo !== undefined && !sameText(original.tempo, draft.tempo)) patch.tempo = value.tempo;

  const legacy = draft.setsTarget === undefined && draft.repsMin === undefined && draft.restSeconds === undefined
    && draft.targetWeightKg === undefined;

  for (const key of ['setsTarget', 'repsMin', 'repsMax', 'restSeconds', 'targetWeightKg']) {
    if (sameNum(original[key] ?? null, value[key])) continue;
    // No formato antigo, o que não se percebeu não se envia (nem se apaga): o valor do servidor fica.
    if (legacy && ((key.startsWith('reps') && unparsed.includes('reps'))
      || (key === 'restSeconds' && unparsed.includes('restTime'))
      || (key === 'targetWeightKg' && unparsed.includes('weight')))) continue;
    patch[key] = value[key];
  }

  for (const field of unparsed) {
    if (legacy && field === 'weight' && sameText(original.weight, draft.weight)) continue;
    if (legacy && field === 'restTime' && sameText(original.restTime, draft.restTime)) continue;
    if (legacy && field === 'reps' && String(original.reps) === String(draft.reps)) continue;
    warnings.push(`${draft.name || 'Exercício'}: "${field}" não foi percebido e ficou como estava`);
  }

  return { patch, warnings };
};

const sameOrder = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/** Conta o que há para gravar, sem gravar nada: serve para avisar "sem alterações". */
export const diffPlan = (original, draft) => {
  const originalDays = original.workoutDays || [];
  const draftDays = draft.workoutDays || [];
  const originalDayIds = new Set(originalDays.map((d) => d.id));

  const header = {
    title: String(draft.title ?? '').trim(),
    description: clean(draft.description),
    notes: clean(draft.notes !== undefined ? draft.notes : original.notes),
    startDate: dateOnly(draft.startDate),
    endDate: dateOnly(draft.endDate),
  };
  const headerChanged = !sameText(original.title, header.title)
    || !sameText(original.description, header.description)
    || !sameText(original.notes, header.notes)
    || (header.startDate && header.startDate !== dateOnly(original.startDate))
    || (header.endDate ?? null) !== dateOnly(original.endDate);

  const removedDays = originalDays.filter((d) => !draftDays.some((x) => x.id === d.id));
  const addedDays = draftDays.filter((d) => !d.id || !originalDayIds.has(d.id));
  const warnings = [];
  let changes = (headerChanged ? 1 : 0) + removedDays.length + addedDays.length;

  const days = [];
  for (const day of draftDays.filter((d) => d.id && originalDayIds.has(d.id))) {
    const before = originalDays.find((d) => d.id === day.id);
    const exercisesBefore = before.exercises || [];
    const exercisesNow = day.exercises || [];
    const removed = exercisesBefore.filter((e) => !exercisesNow.some((x) => x.id === e.id));
    const added = exercisesNow.filter((e) => !e.id || !exercisesBefore.some((b) => b.id === e.id));
    const edited = [];
    for (const ex of exercisesNow.filter((e) => e.id && exercisesBefore.some((b) => b.id === e.id))) {
      const { patch, warnings: w } = exercisePatch(exercisesBefore.find((b) => b.id === ex.id), ex);
      warnings.push(...w);
      if (Object.keys(patch).length > 0) edited.push({ id: ex.id, patch });
    }
    const dayChanged = !sameText(before.dayName, day.dayName) || !sameText(before.notes, day.notes);
    const keptNow = exercisesNow.filter((e) => e.id && exercisesBefore.some((b) => b.id === e.id)).map((e) => e.id);
    const keptBefore = exercisesBefore.filter((e) => keptNow.includes(e.id)).map((e) => e.id);
    const reordered = !sameOrder(keptNow, keptBefore);
    changes += removed.length + added.length + edited.length + (dayChanged ? 1 : 0) + (reordered ? 1 : 0);
    days.push({ day, before, removed, added, edited, dayChanged, reordered });
  }

  const keptDayIds = draftDays.filter((d) => d.id && originalDayIds.has(d.id)).map((d) => d.id);
  const keptDayIdsBefore = originalDays.filter((d) => keptDayIds.includes(d.id)).map((d) => d.id);
  const daysReordered = !sameOrder(keptDayIds, keptDayIdsBefore)
    || draftDays.some((d, i) => (!d.id || !originalDayIds.has(d.id)) && draftDays.slice(i + 1).some((x) => x.id && originalDayIds.has(x.id)));
  if (daysReordered) changes += 1;

  return { header, headerChanged, removedDays, addedDays, days, daysReordered, changes, warnings };
};

/**
 * Grava a diferença, pela ordem: cabeçalho → dias removidos → dias novos (com os seus exercícios) → para cada
 * dia que já existia: remover, alterar, acrescentar e reordenar exercícios → reordenar dias.
 * Pára no primeiro erro (que sobe a quem chamou, com a mensagem do servidor); o que já ficou gravado fica.
 * Devolve { changes, warnings }.
 */
export const applyPlanDiff = async (client, planId, original, draft) => {
  const diff = diffPlan(original, draft);

  if (diff.headerChanged) {
    await client.update(planId, {
      title: diff.header.title,
      description: diff.header.description,
      notes: diff.header.notes,
      startDate: diff.header.startDate ? new Date(diff.header.startDate).toISOString() : undefined,
      endDate: diff.header.endDate ? new Date(diff.header.endDate).toISOString() : null,
    });
  }

  for (const day of diff.removedDays) await client.deleteDay(planId, day.id);

  const draftDays = draft.workoutDays || [];
  const idByDraftDay = new Map();
  for (const day of draftDays.filter((d) => d.id)) idByDraftDay.set(day, day.id);

  for (const entry of diff.days) {
    const { day, before } = entry;

    for (const ex of entry.removed) await client.removeExercise(planId, ex.id);

    if (entry.dayChanged) {
      await client.updateDay(planId, day.id, { dayName: clean(day.dayName) ?? before.dayName, notes: clean(day.notes) ?? '' });
    }

    for (const ex of entry.edited) await client.updateExercise(planId, ex.id, ex.patch);

    const idByDraftExercise = new Map();
    for (const ex of (day.exercises || []).filter((e) => e.id)) idByDraftExercise.set(ex, ex.id);
    for (const ex of entry.added) {
      const res = await client.addExercise(planId, day.id, newExerciseBody(ex));
      idByDraftExercise.set(ex, res.data.id);
    }

    const desired = (day.exercises || []).map((e) => idByDraftExercise.get(e));
    const afterAdds = [
      ...(before.exercises || []).filter((e) => desired.includes(e.id)).map((e) => e.id),
      ...entry.added.map((e) => idByDraftExercise.get(e)),
    ];
    if (desired.length > 1 && !sameOrder(desired, afterAdds)) await client.reorderExercises(planId, day.id, desired);
  }

  for (const day of diff.addedDays) {
    const res = await client.addDay(planId, {
      dayName: clean(day.dayName) || 'Dia',
      notes: clean(day.notes) ?? undefined,
      exercises: (day.exercises || []).map((ex, i) => ({ ...newExerciseBody(ex), orderIndex: i })),
    });
    idByDraftDay.set(day, res.data.id);
  }

  if (diff.daysReordered) {
    const desired = draftDays.map((d) => idByDraftDay.get(d));
    await client.reorderDays(planId, desired);
  }

  return { changes: diff.changes, warnings: diff.warnings };
};

/** O corpo para CRIAR um plano (ou um modelo) com a estrutura inicial: aqui sim, vai tudo de uma vez. */
export const createPlanBody = (draft, { planType = 'MemberPlan', userId = null } = {}) => ({
  planType,
  userId: planType === 'Template' ? null : Number(userId),
  title: String(draft.title ?? '').trim(),
  description: clean(draft.description),
  notes: clean(draft.notes),
  startDate: draft.startDate ? new Date(draft.startDate).toISOString() : new Date().toISOString(),
  endDate: draft.endDate ? new Date(draft.endDate).toISOString() : null,
  workoutDays: (draft.workoutDays || []).map((day, i) => ({
    dayName: clean(day.dayName) || `Dia ${i + 1}`,
    orderIndex: i,
    notes: clean(day.notes),
    exercises: (day.exercises || []).map((ex, j) => ({ ...newExerciseBody(ex), orderIndex: j })),
  })),
});
